import * as React from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { CountryChart } from '../../../../../components/app/chart/country-chart';
import { SectorChart } from '../../../../../components/app/chart/sector-chart';
import { Slideshow } from '../../../../../components/app/swiper/swiper';
import { SkeletonDashboardCharts } from '../../../../../components/app/skeleton/dashboard/trades/charts/skeleton-dashboard-charts';
import { useRootStore } from '../../../../../providers/root-store-provider';
import { Section } from '../../../../../components/app/section/section';
import { Responsive } from '../../../../../components/app/responsive/responsive';
import { AssetTypeChart } from '../../../../../components/app/chart/asset-type-chart';
import { AssetDistributionChart } from '../../../../../components/app/chart/asset-distribution-chart';
import { CurrencyChart } from '../../../../../components/app/chart/currency-chart';
import { Grid } from '../../../../../components/app/grid/grid';
import { NoData } from '../../../../../components/app/no-data/no-data';
import { DashboardChartsItem } from './item/dashboard-trades-charts-item';
import { BrokerChart } from '../../../../../components/app/chart/broker-chart';

export const DashboardTradesCharts: React.FC = observer(() => {
    const { t } = useTranslation();
    const { statisticStore, portfolioStore } = useRootStore();
    const { ratiosStore } = statisticStore;

    if ((ratiosStore.loading && !ratiosStore.hasRatios) || portfolioStore.selectedPortfolio.isRecalculating) {
        return <SkeletonDashboardCharts />;
    }

    if (!ratiosStore.hasRatios) {
        return (
            <div className="overflow-hidden pt-small pb-xlarge">
                <NoData>{t('layout.tables.noDataForRatios')}</NoData>
            </div>
        );
    }

    return (
        <div className="pt-small pb-xlarge max-vl:overflow-hidden">
            <Section>
                <Responsive breakpoint={'vl'} type={'max'}>
                    <Slideshow>
                        <div className="flex h-full flex-col rounded-large border border-border p-medium [&>*]:h-full vl:max-xl:p-vsmall">
                            <DashboardChartsItem
                                title={t('pages.index.graphTitle.assetType')}
                                chart={<AssetTypeChart />}
                            />
                        </div>
                        <div className="flex h-full flex-col rounded-large border border-border p-medium [&>*]:h-full vl:max-xl:p-vsmall">
                            <DashboardChartsItem
                                title={t('pages.index.graphTitle.portfolio')}
                                chart={<AssetDistributionChart />}
                            />
                        </div>
                        <div className="flex h-full flex-col rounded-large border border-border p-medium [&>*]:h-full vl:max-xl:p-vsmall">
                            <DashboardChartsItem
                                title={t('pages.index.graphTitle.currency')}
                                chart={<CurrencyChart orientation={'horizontal'} />}
                            />
                        </div>
                        <div className="flex h-full flex-col rounded-large border border-border p-medium [&>*]:h-full vl:max-xl:p-vsmall">
                            <DashboardChartsItem title={t('pages.index.graphTitle.country')} chart={<CountryChart />} />
                        </div>
                        <div className="flex h-full flex-col rounded-large border border-border p-medium [&>*]:h-full vl:max-xl:p-vsmall">
                            <DashboardChartsItem
                                title={t('pages.index.graphTitle.brokerName')}
                                chart={<BrokerChart />}
                            />
                        </div>
                        <div className="flex h-full flex-col rounded-large border border-border p-medium [&>*]:h-full vl:max-xl:p-vsmall">
                            <DashboardChartsItem title={t('pages.index.graphTitle.sector')} chart={<SectorChart />} />
                        </div>
                    </Slideshow>
                </Responsive>
                <Responsive breakpoint={'vl'} type={'min'}>
                    <Grid cols={{ xs: 1, md: 2, vl: 3 }}>
                        <Grid cols={{ xs: 1 }}>
                            <div className="flex h-full flex-col rounded-large border border-border p-medium [&>*]:h-full vl:max-xl:p-vsmall">
                                <DashboardChartsItem
                                    title={t('pages.index.graphTitle.assetType')}
                                    chart={<AssetTypeChart />}
                                />
                            </div>
                            <div className="flex h-full flex-col rounded-large border border-border p-medium [&>*]:h-full vl:max-xl:p-vsmall">
                                <DashboardChartsItem
                                    title={t('pages.index.graphTitle.portfolio')}
                                    chart={<AssetDistributionChart />}
                                />
                            </div>
                        </Grid>
                        <Grid cols={{ xs: 1 }}>
                            <div className="flex h-full flex-col rounded-large border border-border p-medium [&>*]:h-full vl:max-xl:p-vsmall">
                                <DashboardChartsItem
                                    title={t('pages.index.graphTitle.currency')}
                                    chart={<CurrencyChart />}
                                />
                            </div>
                            <div className="flex h-full flex-col rounded-large border border-border p-medium [&>*]:h-full vl:max-xl:p-vsmall">
                                <DashboardChartsItem
                                    title={t('pages.index.graphTitle.brokerName')}
                                    chart={<BrokerChart />}
                                />
                            </div>
                        </Grid>
                        <Grid cols={{ xs: 1 }}>
                            <div className="flex h-full flex-col rounded-large border border-border p-medium [&>*]:h-full vl:max-xl:p-vsmall">
                                <DashboardChartsItem
                                    title={t('pages.index.graphTitle.country')}
                                    chart={<CountryChart />}
                                />
                            </div>
                            <div className="flex h-full flex-col rounded-large border border-border p-medium [&>*]:h-full vl:max-xl:p-vsmall">
                                <DashboardChartsItem
                                    title={t('pages.index.graphTitle.sector')}
                                    chart={<SectorChart />}
                                />
                            </div>
                        </Grid>
                    </Grid>
                </Responsive>
            </Section>
        </div>
    );
});
